// Client-side search over conflict and battle names (the index is loaded on first use).

import type { SearchIndex } from "./data";

export interface Hit { name: string; kind: "c" | "e" | "u"; ref: number | string; t: number; score: number; alias?: string }

export function normalise(s: string): string {
  return s.normalize("NFKD").replace(/[̀-ͯ]/g, "").toLowerCase().replace(/[–—-]/g, " ").replace(/[^a-z0-9 ]+/g, "")
    .replace(/\s+/g, " ").trim();
}

export interface Prepared { idx: SearchIndex; names: string[]; aliases: string[][] }

export function prepare(idx: SearchIndex): Prepared {
  return {
    idx,
    names: idx.n.map(normalise),
    aliases: idx.a.map((a) => (a ? a.split("|").map(normalise) : [])),
  };
}

const LEAD = /^(the |battle of the |battle of |battles of |siege of the |siege of |sack of |fall of |capture of |massacre of |war of the |war of |invasion of |operation )/;
const WWII = /^(ww ?2|ww ?ii|wwii|second world war)$/;
const WWI = /^(ww ?1|ww ?i|wwi|first world war|great war)$/;

/** Ranked matches: exact > prefix > word prefix > substring; prominence breaks ties. */
export function search(p: Prepared, query: string, limit = 12): Hit[] {
  let q = normalise(query);
  if (!q) return [];
  if (WWII.test(q)) q = "world war ii";
  else if (WWI.test(q)) q = "world war i";
  const words = q.split(" ");
  const scored: Hit[] = [];
  const consider = (text: string, i: number, alias: boolean) => {
    let base = -1;
    const core = text.replace(LEAD, ""); // "battle of waterloo" → "waterloo"
    if (text === q || core === q) base = 1000;
    else if (text.startsWith(q) || core.startsWith(q)) base = 700;
    else if (words.every((w) => text.split(" ").some((t) => t.startsWith(w)))) base = 500;
    else if (text.includes(q)) base = 300;
    if (base < 0) return null;
    const s = p.idx.s[i];
    return base + s / 4 - (alias ? 60 : 0) - Math.min(text.length, 80) * 0.5 + (p.idx.k[i] === "c" ? 25 : 0);
  };
  for (let i = 0; i < p.names.length; i++) {
    let best = consider(p.names[i], i, false);
    let viaAlias: string | undefined;
    for (const a of p.aliases[i]) {
      const sc = consider(a, i, true);
      if (sc != null && (best == null || sc > best)) { best = sc; viaAlias = a; }
    }
    if (best == null) continue;
    scored.push({ name: p.idx.n[i], kind: p.idx.k[i] as Hit["kind"], ref: p.idx.i[i], t: p.idx.t[i], score: best, alias: viaAlias });
  }
  scored.sort((a, b) => b.score - a.score);
  // de-duplicate identical names, keeping the most relevant
  const seen = new Set<string>();
  const out: Hit[] = [];
  for (const h of scored) {
    const key = `${h.name}|${Math.round(h.t)}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(h);
    if (out.length >= limit) break;
  }
  return out;
}
